import React, { useCallback, useState, type SyntheticEvent } from 'react';
import {
	SpotlightManager,
	SpotlightPulse,
	SpotlightTarget,
	SpotlightTransition,
} from '@atlaskit/onboarding';
import { token } from '@atlaskit/tokens';
import CoordinationClient from '@atlassian/jira-engagement/src/ui/coordination-client/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useLeftSidebarState } from '@atlassian/jira-layout-controller/src/controllers/layout-controller/consumers/left-sidebar-controller/index.tsx';
import {
	fireTrackAnalytics,
	ContextualAnalyticsData,
	FireScreenAnalytics,
	type UIAnalyticsEvent,
} from '@atlassian/jira-product-analytics-bridge';
import { JiraSpotlight } from '@atlassian/jira-spotlight/src/ui/jira-spotlight.tsx';
import messages from './messages.tsx';

type Props = {
	children: React.ReactElement;
};
type StopFunction = () => void;
interface CoordinationStopProviderProps {
	stop?: StopFunction;
	children: (arg1: StopFunction | undefined) => React.ReactElement;
}

const CoordinationStopProvider = ({ stop, children }: CoordinationStopProviderProps) =>
	children(stop);

const TARGET = 'plan-settings-menu-trigger';
const MESSAGE_ID = 'ploverview-sidebar-menu';

const Nudge = ({
	stop,
	children,
}: {
	stop: StopFunction | undefined;
	children: React.ReactElement;
}) => {
	const { isCollapsed: isLeftSideBarCollapsed } = useLeftSidebarState();
	const { formatMessage } = useIntl();
	const [isSpotlightActive, setIsSpotlightActive] = useState<boolean>(true);

	const onClick = useCallback(
		(event: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
			fireTrackAnalytics(analyticsEvent, 'planHeaderMenuSpotlightNudge dismissed');
			stop?.();
			setIsSpotlightActive(false);
		},
		[stop],
	);
	if (isLeftSideBarCollapsed || !isSpotlightActive) {
		return children;
	}

	return (
		<ContextualAnalyticsData sourceName="planHeaderMenuSpotlightNudge" sourceType="Screen">
			<SpotlightManager>
				<SpotlightTarget name={TARGET}>
					<SpotlightPulse radius={3}>{children}</SpotlightPulse>
				</SpotlightTarget>
				<SpotlightTransition>
					<JiraSpotlight
						actions={[
							{
								onClick,
								text: formatMessage(messages.dismiss),
							},
						]}
						heading={formatMessage(messages.title)}
						target={TARGET}
						key={TARGET}
						targetRadius={3}
						targetBgColor={token('elevation.surface')}
						messageId="navigation-apps-sidebar-plan.ui.menu.header.nudge.jira-spotlight"
						messageType="engagement"
					>
						{formatMessage(messages.description)}
					</JiraSpotlight>
				</SpotlightTransition>
			</SpotlightManager>
			<FireScreenAnalytics />
		</ContextualAnalyticsData>
	);
};

const NudgeProvider = ({ children }: Props) => (
	<CoordinationClient messageId={MESSAGE_ID} fallback={children} messageType="engagement">
		<CoordinationStopProvider>
			{(stop) => <Nudge stop={stop}>{children}</Nudge>}
		</CoordinationStopProvider>
	</CoordinationClient>
);

export default NudgeProvider;
